import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";

interface Choice {
    id: number;
    text: string;
}

interface Question {
    id: number;
    text: string;
    image_url?: string;
    choices: Choice[];
}

export default function CandidateTestPage() {
    const { testId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");

    // ✅ Add candidateId state
    const [candidateId, setCandidateId] = useState<string | null>(null);

    // ✅ Initial load
    useEffect(() => {
        async function initialize() {
            setLoading(true);
            setMessage("");

            try {
                // 1️⃣ Validate auth
                const {
                    data: { user },
                    error: userError,
                } = await supabase.auth.getUser();

                if (userError || !user) {
                    navigate("/candidate/login");
                    return;
                }

                // 2️⃣ Find candidate
                const { data: candidate, error: candidateError } = await supabase
                    .from("candidates")
                    .select("id, mugshot_url, embedding_vector")
                    .or(`email.eq.${user.email},phone.eq.${user.phone}`)
                    .single();

                if (candidateError || !candidate) {
                    console.error("Candidate not found:", candidateError);
                    navigate("/candidate/unauthorized");
                    return;
                }

                setCandidateId(candidate.id);

                // 3️⃣ Require enrollment (biometric check)
                if (!candidate.mugshot_url || !candidate.embedding_vector) {
                    navigate("/candidate/enroll", { state: { candidateId: candidate.id } });
                    return;
                }

                // 4️⃣ Fetch test questions
                const { data: questions, error: questionError } = await supabase
                    .from("questions")
                    .select("id, text, image_url, choices(id, text)")
                    .eq("test_id", testId)
                    .order("id");

                if (questionError) throw questionError;
                if (!questions?.length) {
                    setMessage("No questions found for this test.");
                    return;
                }

                setQuestions(questions as Question[]);

                // 5️⃣ Load previously saved answers from exam_attempts
                const { data: attempts, error: attemptsError } = await supabase
                    .from("exam_attempts")
                    .select("question_id, choice_id")
                    .eq("test_id", testId)
                    .eq("candidate_id", candidate.id);

                if (attemptsError) console.warn("No prior answers found:", attemptsError);

                // ✅ Convert attempts into a dictionary { questionId: choiceId }
                const prefilled: Record<number, number> = {};
                (attempts || []).forEach((a) => {
                    prefilled[a.question_id] = a.choice_id;
                });
                setAnswers(prefilled);

                // 6️⃣ Restore question position
                const qId = searchParams.get("q");
                if (qId) {
                    const idx = questions.findIndex((q: any) => q.id === Number(qId));
                    if (idx >= 0) setCurrentIndex(idx);
                } else {
                    const savedIndex = localStorage.getItem("currentQuestionIndex");
                    if (savedIndex) setCurrentIndex(Number(savedIndex));
                }
            } catch (err: any) {
                console.error("❌ Error loading test:", err);
                setMessage(err.message || "An unexpected error occurred.");
            } finally {
                setLoading(false);
            }
        }

        initialize();
    }, [testId, navigate]);

    // ✅ Persist current question index locally
    useEffect(() => {
        localStorage.setItem("currentQuestionIndex", currentIndex.toString());
    }, [currentIndex]);

    // ✅ Save answer to exam_attempts
    async function handleAnswer(questionId: number, choiceId: number) {
        if (saving || !candidateId) return; // candidateId must exist
        setAnswers((prev) => ({ ...prev, [questionId]: choiceId }));
        setSaving(true);

        try {
            const { error } = await supabase.from("exam_attempts").upsert(
                {
                    candidate_id: candidateId, // ✅ correct UUID from candidates table
                    test_id: Number(testId),
                    question_id: questionId,
                    choice_id: choiceId,
                    answered_at: new Date().toISOString(),
                },
                { onConflict: "candidate_id,test_id,question_id" }
            );

            if (error) console.error("❌ Error saving answer:", error);
            else console.log("✅ Saved:", { questionId, choiceId });
        } catch (err) {
            console.error("❌ Unexpected error saving answer:", err);
        } finally {
            setSaving(false);
        }
    }

    // ✅ Navigation
    const nextQuestion = () =>
        setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));

    const prevQuestion = () =>
        setCurrentIndex((prev) => Math.max(prev - 1, 0));

    const handleSubmitTest = () => navigate(`/candidate/summary/${testId}`);

    // 🔊 Text-to-speech
    function playQuestionAndChoices(question: Question) {
        const synth = window.speechSynthesis;
        if (!synth) return alert("Speech synthesis not supported.");

        const sequence: SpeechSynthesisUtterance[] = [];
        sequence.push(new SpeechSynthesisUtterance(`Question: ${question.text}`));
        question.choices.forEach((c, i) =>
            sequence.push(new SpeechSynthesisUtterance(`Option ${i + 1}: ${c.text}`))
        );
        sequence.forEach((utt) => synth.speak(utt));
    }

    // ✅ UI states
    if (loading) return <p className="p-6 text-center">Loading test...</p>;
    if (message) return <p className="p-6 text-center text-red-500">{message}</p>;
    if (questions.length === 0)
        return <p className="p-6 text-center">No questions available.</p>;

    const current = questions[currentIndex];
    const hasImageHelper = Boolean(current.image_url);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50 flex items-center justify-center py-8">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <h1 className="text-center text-2xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-500">
                    Test #{testId}
                </h1>

                <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Question {currentIndex + 1} of {questions.length}
                        </h2>

                        <div className="flex gap-2">
                            {/* 📊 Summary Button */}
                            <button
                                onClick={() =>
                                    navigate(
                                        `/candidate/summary/${testId}?q=${questions[currentIndex].id}`
                                    )
                                }
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-sm border border-gray-300 transition"
                            >
                                📊 Summary
                            </button>

                            {/* 🔊 Play Button */}
                            <button
                                onClick={() => playQuestionAndChoices(current)}
                                className="flex items-center gap-1 bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1.5 rounded-full text-sm shadow transition-all"
                            >
                                🔊 <span className="font-medium">Play</span>
                            </button>
                        </div>
                    </div>

                    <div
                        className="mb-4 text-gray-700 text-base leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: current.text }}
                    />

                    {hasImageHelper ? (
                        <>
                            <img
                                src={current.image_url}
                                alt="Question illustration"
                                className="mb-4 max-h-64 object-contain border rounded-lg shadow-sm"
                            />
                        </>
                    ) : (
                        <div className="mb-4 text-xs text-gray-400 italic">
                            (No image helper for this question)
                        </div>
                    )}

                    <div className="space-y-3">
                        {current.choices.map((c) => (
                            <label
                                key={c.id}
                                className={`flex items-center gap-3 border rounded-xl px-4 py-3 transition-all cursor-pointer text-gray-800 ${answers[current.id] === c.id
                                        ? "bg-indigo-50 border-indigo-400 shadow-sm"
                                        : "hover:bg-gray-50 border-gray-200"
                                    }`}
                            >
                                <input
                                    type="radio"
                                    name={`q-${current.id}`}
                                    value={c.id}
                                    checked={answers[current.id] === c.id}
                                    onChange={() => handleAnswer(current.id, c.id)}
                                    disabled={saving}
                                    className="text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-sm">{c.text}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="flex justify-between mt-8">
                    <button
                        onClick={prevQuestion}
                        disabled={currentIndex === 0}
                        className="px-6 py-2.5 bg-gray-200 text-gray-600 rounded-full font-medium disabled:opacity-50 hover:bg-gray-300 transition"
                    >
                        Previous
                    </button>

                    {currentIndex < questions.length - 1 ? (
                        <button
                            onClick={nextQuestion}
                            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-500 text-white rounded-full font-semibold shadow hover:opacity-90 transition"
                        >
                            Next
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmitTest}
                            className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full font-semibold shadow hover:opacity-90 transition"
                        >
                            Submit Test
                        </button>
                    )}
                </div>

                {saving && (
                    <p className="mt-5 text-sm text-gray-500 text-center animate-pulse">
                        💾 Saving your answer...
                    </p>
                )}
            </div>
        </div>
    );
}
